import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SMART_ROUTES } from '../../constants';

export interface SmartGalleryNotice {
  id: string;
  title: string;
  body: string;
  isRead: boolean;
  createdAt?: string;
}

export const SMART_GALLERY_NOTICE_FIXTURES = [
  {
    id: 'n1',
    titleKey: 'notifications.exampleSubject',
    bodyKey: 'notifications.exampleBody',
    read: false,
    createdAt: '2026-10-28T15:30:00.000Z',
  },
  {
    id: 'n2',
    titleKey: 'notifications.exampleSubject2',
    bodyKey: 'notifications.exampleBody2',
    read: true,
    createdAt: '2026-10-27T11:00:00.000Z',
  },
  {
    id: 'n3',
    titleKey: 'notifications.exampleSubject3',
    bodyKey: 'notifications.exampleBody3',
    read: false,
    createdAt: '2026-10-26T09:15:00.000Z',
  },
] as const;

export function SmartGalleryBell({
  unread,
  onOpen,
}: {
  unread: number;
  onOpen: () => void;
}) {
  const { t } = useTranslation('smart');
  return (
    <button
      type="button"
      aria-label={t('notifications.bellLabel')}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
      className="border-border bg-bg-elevated text-fg-muted hover:text-fg relative inline-flex h-9 w-9 items-center justify-center rounded-full border"
    >
      <Bell size={16} aria-hidden />
      {unread > 0 ? (
        <span className="bg-accent text-accent-fg absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold">
          {unread}
        </span>
      ) : null}
    </button>
  );
}

/** Default chrome bell: gallery always demos inbox; opens dedicated inbox screen. */
export function SmartGalleryChromeBell() {
  const navigate = useNavigate();
  const unread = SMART_GALLERY_NOTICE_FIXTURES.filter((item) => !item.read)
    .length;
  return (
    <SmartGalleryBell
      unread={unread}
      onOpen={() => navigate(SMART_ROUTES.INBOX)}
    />
  );
}

export function SmartGalleryDrawer({
  items,
  open,
  onClose,
  onMarkRead,
  onArchive,
  onOpenPreferences,
}: {
  items: SmartGalleryNotice[];
  open: boolean;
  onClose: () => void;
  onMarkRead: (id: string) => void;
  onArchive: (id: string) => void;
  onOpenPreferences: () => void;
}) {
  const { t, i18n } = useTranslation('smart');
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
      onClick={onClose}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={t('notifications.title')}
        className="bg-bg flex max-h-[85dvh] w-full max-w-md flex-col rounded-t-2xl px-6 pt-5 pb-8"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold">{t('notifications.title')}</h2>
          <button
            type="button"
            className="text-fg-muted text-sm font-semibold"
            onClick={onClose}
          >
            {t('notifications.close')}
          </button>
        </header>
        {items.length === 0 ? (
          <p className="text-fg-muted py-6 text-sm">{t('notifications.empty')}</p>
        ) : (
          <ul className="min-h-0 flex-1 space-y-4 overflow-y-auto pb-4">
            {items.map((item) => (
              <li
                key={item.id}
                data-read={item.isRead}
                className={`border-border border-b pb-4 last:border-0${
                  item.isRead ? ' opacity-70' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  {!item.isRead ? (
                    <span
                      className="inline-block shrink-0 rounded-full"
                      style={{
                        width: 8,
                        height: 8,
                        backgroundColor: 'var(--color-accent)',
                      }}
                      aria-hidden
                    />
                  ) : null}
                  <strong className="min-w-0 text-sm font-semibold">
                    {item.title}
                  </strong>
                </div>
                {item.body ? (
                  <p className="text-fg-muted mt-1 text-sm">{item.body}</p>
                ) : null}
                {item.createdAt ? (
                  <time className="text-fg-muted mt-1 block text-xs">
                    {new Intl.DateTimeFormat(i18n.language, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    }).format(new Date(item.createdAt))}
                  </time>
                ) : null}
                <div className="mt-2 flex gap-4">
                  {!item.isRead ? (
                    <button
                      type="button"
                      className="text-accent p-0 !text-[0.85rem] font-bold leading-none"
                      onClick={() => onMarkRead(item.id)}
                    >
                      {t('notifications.markRead')}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="text-accent p-0 !text-[0.85rem] font-bold leading-none"
                    onClick={() => onArchive(item.id)}
                  >
                    {t('notifications.archive')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="text-fg-muted mt-2 text-xs">{t('notifications.retention')}</p>
        <button
          type="button"
          className="bg-accent text-accent-fg mt-4 w-full rounded-xl px-6 py-3 text-sm font-semibold"
          onClick={onOpenPreferences}
        >
          {t('notifications.preferences')}
        </button>
      </section>
    </div>
  );
}

export function SmartGalleryPreferences({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('smart');
  if (!open) return null;
  const channels = [
    { id: 'in_app', label: t('notifications.channelInApp'), on: true },
    { id: 'email', label: t('notifications.channelEmail'), on: true },
    { id: 'sms', label: t('notifications.channelSms'), on: false },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
      onClick={onClose}
    >
      <section
        role="dialog"
        aria-modal="true"
        className="bg-bg w-full max-w-md rounded-t-2xl px-6 pt-5 pb-8"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold">
            {t('notifications.preferencesTitle')}
          </h2>
          <button
            type="button"
            className="text-fg-muted text-sm font-semibold"
            onClick={onClose}
          >
            {t('notifications.close')}
          </button>
        </header>
        <ul className="space-y-3">
          {channels.map((row) => (
            <li key={row.id} className="flex items-center justify-between gap-3">
              <span className="text-sm">{row.label}</span>
              <span
                className={`relative h-7 w-12 rounded-full ${
                  row.on ? 'bg-accent' : 'bg-border'
                }`}
                aria-hidden
              >
                <span
                  className={`bg-bg absolute top-0.5 left-0.5 h-6 w-6 rounded-full ${
                    row.on ? 'translate-x-5' : ''
                  }`}
                />
              </span>
            </li>
          ))}
        </ul>
        <p className="text-fg-muted mt-4 text-xs">
          {t('notifications.preferencesEssential')}
        </p>
      </section>
    </div>
  );
}

/** Gallery toast — same chrome/sizing as live NotificationsToast (stays on for review). */
export function SmartGalleryToast({ message }: { message: string }) {
  return (
    <div
      role="status"
      className="bg-bg-elevated border-border fixed top-4 left-1/2 z-[70] w-max max-w-[min(28rem,calc(100%-2rem))] -translate-x-1/2 rounded-xl border px-4 py-3 text-sm break-words whitespace-normal shadow-lg"
    >
      {message}
    </div>
  );
}
